import { mediaRef } from '@/services/mediaAssets';
import valleyOfFireGallery from './valleyOfFireGallery';
import zionHikeGallery from './zionHikeGallery';
import zionStoryPages from './zionStory';
import { achievementRefs, tagRefs } from '../../../reckonWayTags';
import {
  getRecurringActivities,
  getRecurringAttendees,
  getRecurringEventStats,
  getRecurringRestaurantVisits,
} from '../../recurringEvent';
import { defineReckonEvent } from '../../schema';

const awsReinventHero = mediaRef('adventures/aws-reinvent-heroes-no-text.png');
const bazaarBitesImage = mediaRef('adventures/aws-reinvent-2023-bazaar-bites.jpg');
const bazaarCottonCandyImage = mediaRef('adventures/aws-reinvent-2023-bazaar-cotton-candy.jpg');
const bazaarSteakImage = mediaRef('adventures/aws-reinvent-2023-bazaar-steak.jpg');
const brezzaDinnerImage = mediaRef('adventures/aws-reinvent-2024-brezza-3025.jpg');
const donsPrimeDinnerImage = mediaRef('adventures/aws-reinvent-2024-dons-prime-3048.jpg');
const stkDinnerImage = mediaRef('adventures/aws-reinvent-2023-stk-6013.jpg');
const stkCrewImage = mediaRef('adventures/aws-reinvent-2023-stk-9530.jpg');
const stkToastImage = mediaRef('adventures/aws-reinvent-2023-stk-9532.jpg');
const stkDinner2024Image = mediaRef('adventures/aws-reinvent-2024-stk-3005.jpg');
const utvCrewGroupImage = mediaRef('adventures/aws-reinvent-2023-utv-crew-group.jpg');
const utvDriverImage = mediaRef('adventures/aws-reinvent-2023-utv-driver.jpg');
const utvTrailBreakImage = mediaRef('adventures/aws-reinvent-2023-utv-trail-break.jpg');
const utvGearUpImage = mediaRef('adventures/aws-reinvent-2023-utv-9554.jpg');
const utvTrailBriefingImage = mediaRef('adventures/aws-reinvent-2023-utv-9555.jpg');
const utvDriverViewImage = mediaRef('adventures/aws-reinvent-2023-utv-9556.jpg');
const utvBehindTheWheelImage = mediaRef('adventures/aws-reinvent-2023-utv-9557.jpg');
const catchFlowerWallImage = mediaRef('adventures/aws-reinvent-2023-catch-9539.jpg');
const catchDinner2023Image = mediaRef('adventures/aws-reinvent-2023-catch-9545.jpg');
const u2SphereCrewImage = mediaRef('adventures/aws-reinvent-2023-u2-sphere-9591.jpg');
const u2SphereConcertImage = mediaRef('adventures/aws-reinvent-2023-u2-sphere-9610.jpg');
const catchTeamDinnerImage = mediaRef('adventures/aws-reinvent-catch-team-dinner.png');
const eaglesAtSphereConcertImage = mediaRef('adventures/aws-reinvent-eagles-at-sphere-concert.jpg');
const eaglesAtSphereGroupImage = mediaRef('adventures/aws-reinvent-eagles-at-sphere-group.png');
const estiatorioMilosSeafoodImage = mediaRef('adventures/aws-reinvent-2024-estiatorio-milos-seafood.jpg');
const goldenSteerGroupImage = mediaRef('adventures/aws-reinvent-golden-steer-group.png');
const whiskeyTastingCrewImage = mediaRef('adventures/aws-reinvent-2024-whiskey-tasting-crew.jpg');
const whiskeysOfTheWorldImage = mediaRef('adventures/aws-reinvent-whiskeys-of-the-world.jpg');
const vegas2022Stk5298Image = mediaRef('adventures/aws-reinvent-2022-5298.jpg');
const vegas2022Stk5302Image = mediaRef('adventures/aws-reinvent-2022-5302.jpg');
const vegas2022Atv5314Image = mediaRef('adventures/aws-reinvent-2022-5314.jpg');
const vegas2022Atv5315Image = mediaRef('adventures/aws-reinvent-2022-5315.jpg');
const vegas2022Atv5318Image = mediaRef('adventures/aws-reinvent-2022-5318.jpg');
const vegas2022Amalfi5383Image = mediaRef('adventures/aws-reinvent-2022-5383.jpg');
const vegas2022Bazaar5396Image = mediaRef('adventures/aws-reinvent-2022-5396.jpg');
const vegas2022Bazaar5398Image = mediaRef('adventures/aws-reinvent-2022-5398.jpg');
const alHeadshot = mediaRef('lames/Al M.jpeg');
const benHeadshot = mediaRef('lames/Ben B.jpeg');
const brandonAHeadshot = mediaRef('lames/Brandon A New.jpeg');
const brandonLHeadshot = mediaRef('lames/Brandon L New.jpeg');
const jasmineHeadshot = mediaRef('lames/Jasmine B.jpeg');
const kevinHeadshot = mediaRef('lames/Kevin L.jpeg');
const marcusHeadshot = mediaRef('lames/Marcus G New.jpeg');
const mattHeadshot = mediaRef('lames/Matt G.jpeg');

const years = [
  {
    year: 2022,
    attendees: ['Kevin L.', 'Brandon A.', 'Brandon L.', 'Marcus'],
    activities: ['ATV Tour in the Mojave Desert'],
    restaurants: ['STK', 'Amalfi by Bobby Flay', 'Bazaar Meat'],
    highlights: [
      'The beginning of Reckon’s recurring re:Invent tradition',
      'ATV adventure in the Mojave Desert',
    ],
    status: 'published',
  },
  {
    year: 2023,
    attendees: ['Kevin L.', 'Brandon A.', 'Brandon L.', 'Marcus', 'Al', 'Matt'],
    activities: ['UTV Tour in the Mojave Desert', 'U2 at Sphere'],
    restaurants: ['STK', 'Catch', 'Bazaar Meat'],
    highlights: [
      'The Reckon Crew expanded',
      'First Reckon visit to Sphere',
      'U2 live at Sphere',
    ],
    status: 'published',
  },
  {
    year: 2024,
    attendees: ['Kevin L.', 'Brandon A.', 'Brandon L.', 'Marcus', 'Al', 'Jasmine', 'Ben', 'Matt'],
    activities: [
      'Jeep Off-Road Tour in Valley of Fire',
      'Hiking Zion National Park',
      'Eagles at Sphere',
      'Whiskeys of the World Tasting',
    ],
    restaurants: ['STK', 'Catch', 'Brezza', 'Golden Steer', 'Don’s Prime', 'Estiatorio Milos'],
    highlights: [
      'Largest Reckon re:Invent crew to date',
      'Off-roading through Valley of Fire',
      'Hiking Zion National Park',
      'Eagles live at Sphere',
    ],
    status: 'published',
  },
  {
    year: 2025,
    attendees: ['Kevin L.', 'Marcus'],
    activities: [],
    restaurants: ['Jean-Georges Steakhouse'],
    highlights: [
      'A smaller return to Las Vegas',
      'Dinner at Jean-Georges Steakhouse',
    ],
    status: 'published',
  },
];

const activityMetadata = {
  'ATV Tour in the Mojave Desert': { category: 'Outdoor Adventure', meritBadgeSlug: 'atvs-mojave-desert' },
  'UTV Tour in the Mojave Desert': { category: 'Outdoor Adventure', meritBadgeSlug: 'utvs-mojave-desert' },
  'U2 at Sphere': { category: 'Live Entertainment', meritBadgeSlug: 'u2-at-sphere' },
  'Jeep Off-Road Tour in Valley of Fire': {
    entityId: 'jeep-off-road-tour-in-valley-of-fire-2024',
    category: 'Outdoor Adventure',
    meritBadgeSlug: 'valley-of-fire-off-roading',
  },
  'Hiking Zion National Park': { category: 'National Park', meritBadgeSlug: 'hiking-zion-national-park' },
  'Eagles at Sphere': { category: 'Live Entertainment', meritBadgeSlug: 'eagles-at-sphere' },
  'Whiskeys of the World Tasting': { category: 'Tasting Experience' },
};

const crewDirectory = {
  'Kevin L.': {
    slug: 'kevin-l', displayName: 'Kevin L.', title: 'Chief Executive Officer', headshot: kevinHeadshot,
  },
  'Brandon A.': {
    slug: 'brandon-a', displayName: 'Brandon A.', title: 'Co-Founder', headshot: brandonAHeadshot,
  },
  'Brandon L.': {
    slug: 'brandon-l', displayName: 'Brandon L.', title: 'Co-Founder', headshot: brandonLHeadshot,
  },
  Marcus: {
    slug: 'marcus-g', displayName: 'Marcus G.', title: 'Co-Founder', headshot: marcusHeadshot,
  },
  Al: {
    slug: 'al-m', displayName: 'Al M.', title: 'Reckon Crew Member', headshot: alHeadshot,
  },
  Matt: {
    slug: 'matt-g', displayName: 'Matt G.', title: 'Software Developer', headshot: mattHeadshot,
  },
  Jasmine: {
    slug: 'jasmine-b', displayName: 'Jasmine B.', title: 'Reckon Crew Member', headshot: jasmineHeadshot,
  },
  Ben: {
    slug: 'ben-b', displayName: 'Ben B.', title: 'Software Developer', headshot: benHeadshot,
  },
};

const activityDisplayMetadata = {
  'ATV Tour in the Mojave Desert': {
    description: 'Our first company conference adventure traded the Las Vegas Strip for a guided ATV ride across the Mojave Desert and helped establish a Reckon tradition.',
    whyWeWent: 'We wanted our first company conference trip to include something memorable beyond sessions and meetings, so the four co-founders left the Strip to try an adventure none of us had done before.',
    whatHappened: 'Best ATV Tours LV took us from the Luxor into the Mojave, where we learned the controls, followed the desert trails, and climbed a towering dune to a panoramic ridgeline.',
    whatWeRemember: 'This was more than an afternoon of off-roading. It became the first chapter of our tradition of building stronger teams through shared adventures.',
    storySections: [
      {
        heading: 'Why We Chose This Adventure',
        paragraphs: [
          'By 2022, I had attended AWS re:Invent enough times to know that while the conference itself was incredibly valuable, spending an entire week inside convention centers and on the Las Vegas Strip was not the experience I wanted our team to remember.',
          'This was the first conference trip Reckon had taken as a company. At the time, the team consisted solely of the four co-founders, Brandon A., Brandon L., Marcus, and me. It was the perfect opportunity to establish the kind of traditions we hoped would grow alongside the company.',
          'Rather than filling every hour with sessions and meetings, we intentionally carved out time to leave the city behind. We wanted to experience something completely different together, something none of us had ever done before. Riding ATVs through the Mojave Desert checked every box. It took us away from the noise of Las Vegas, put everyone on equal footing as beginners, and gave us the chance to create memories that had nothing to do with conference badges or keynote presentations.',
          'Looking back, this was not simply an afternoon of off-roading.',
          'It became the first chapter of a tradition that would eventually define The Reckon Way.',
        ],
      },
      {
        heading: 'Escaping the Strip',
        paragraphs: [
          'After attending a Wednesday morning session at AWS re:Invent, it was time to trade conference halls for open desert.',
          'The plan was simple. Meet at the Luxor Hotel at 10:45 a.m., where our tour shuttle would pick us up.',
          'I grabbed a taxi from the conference and arrived to find Brandon A. and Marcus already waiting outside.',
          'The only person missing was Brandon L.',
          'A quick text revealed the problem. He was somewhere inside the sprawling Luxor casino trying to find the correct exit. Like many first-time visitors to Las Vegas, he had discovered that getting through a casino can sometimes be more confusing than navigating the conference itself. After a few messages and some improvised directions, he finally emerged, and our crew was together.',
          'A white shuttle van with the Best ATV Tours LV logo pulled up to the curb. We climbed aboard, where our driver and guide, Alex, welcomed us before handing around a tablet so everyone could complete the required waivers.',
          'With the paperwork finished, we settled in for the roughly fifty-minute drive into the Mojave Desert.',
          'As the casinos disappeared in the rearview mirror, the city slowly gave way to wide-open desert.',
          'It felt like we were leaving one world behind and entering another.',
        ],
      },
      {
        heading: 'Base Camp',
        paragraphs: [
          'Eventually we pulled into what looked like a simple park-and-ride lot surrounded by miles of desert.',
          'There was not a visitor center or elaborate staging area. There was just a gravel lot, a row of ATVs parked beside a large trailer that had clearly delivered them before sunrise, and a smaller covered trailer where helmets, goggles, and gloves were handed out.',
          'Alex gathered us together for a safety briefing.',
          'He walked us through the controls, explained the hand signals we would use during the ride, and made sure everyone was comfortable before we headed onto the trail.',
          'Waiting for us were Tracker Off Road 450 ATVs.',
          'Climbing onto one immediately changed my perspective.',
          'These were not recreational toys.',
          'Each machine weighed around 600 pounds, and the moment I settled into the seat I realized this was going to be far more physical than I had imagined.',
          'One by one, the engines came to life, replacing the quiet desert with the deep rumble of four idling ATVs.',
          'After a short practice lap around the parking area to get comfortable with the controls, we lined up behind Alex at the trailhead.',
          'The pavement was behind us.',
          'Ahead stretched miles of Mojave Desert.',
        ],
      },
      {
        heading: 'Finding Our Rhythm',
        paragraphs: [
          'Alex eased onto the trail, and we followed in single file.',
          'Those first few minutes were spent somewhere between excitement and caution. None of us had driven an ATV before, and our immediate goal was not to go fast.',
          'It was simply to keep Alex in sight.',
          'The trail wound through deep sand, loose gravel, rocky washes, and rolling dunes. Every few minutes the terrain changed, demanding something different from both the rider and the machine.',
          'Each ATV carried an eight-foot safety flag mounted behind the seat.',
          'Ironically, we rarely noticed it.',
          'The easiest way to follow Alex was not the flag. It was the enormous cloud of dust his ATV kicked into the air. That drifting trail of sand became our guide through the desert.',
          'At first, Alex looked back every few seconds to make sure everyone was still with him.',
          'As we grew more comfortable, something changed.',
          'Those quick glances became full inspections.',
          'He would stand on the foot pegs, twist completely around, steer with one hand, and somehow continue weaving through the trail without ever looking where he was going.',
          'Meanwhile, I was gripping the handlebars with both hands, completely focused on staying upright.',
          'My only thought was: "What a show off."',
          'Looking back, it was not showing off at all.',
          'It was experience.',
          'Alex had probably driven these trails hundreds of times.',
          'To four first-time riders, though, it looked almost effortless.',
        ],
      },
      {
        heading: 'A Break in the Desert',
        paragraphs: [
          'After twenty or thirty minutes of riding, Alex led us to a stop beside a large rock outcrop that offered one of the few patches of shade for miles.',
          'We shut off the engines and removed our helmets.',
          'The silence was almost startling after the constant rumble of the ride.',
          'Although the desert sun was bright, the late-November air was surprisingly chilly. Standing in the shade, you could definitely feel it. Step back into the sunlight, and the warmth immediately returned.',
          'We grabbed bottles of water and spent a few minutes taking in the landscape.',
          'Mountains stretched across the horizon.',
          'Trails disappeared into sand and rock.',
          'There was not another group in sight.',
          'It was hard to believe that less than two hours earlier we had been surrounded by tens of thousands of people inside one of the world\'s largest technology conferences.',
          'Now it was just five people standing in the middle of the Mojave Desert.',
          'As we finished our water and started putting our helmets back on, Alex smiled.',
          '"The ride out here was just the warm-up."',
          'Then he added, "Now we\'re really going to fly."',
        ],
      },
      {
        heading: 'The Dune',
        paragraphs: [
          'The pace immediately picked up.',
          'We carved through a series of sweeping S-curves between rolling sand dunes, each turn faster than the last. By now, we trusted both the ATVs and ourselves. Instead of thinking about the controls, we were simply enjoying the ride.',
          'Then we rounded one final corner.',
          'Directly in front of us stood the largest sand dune I had ever seen.',
          'It looked more like a mountain than a dune.',
          'The trail climbed straight toward it before disappearing over the top.',
          'Alex pulled off to the right side of the trail and motioned for us to pass.',
          'No explanation.',
          'No stopping.',
          'Just the unmistakable signal to go.',
          'We all understood exactly what he meant.',
          'One by one, we accelerated toward the base of the dune.',
          'The engines roared as the ATVs dug into the soft sand.',
          'At first the climb felt easy.',
          'Then the incline grew steeper.',
          'The ATV slowed.',
          'For a moment I wondered if it was actually going to make it.',
          'It never stopped.',
          'Foot by foot, it continued climbing.',
          'The ascent felt endless.',
          'Then, almost without realizing it, the front wheels reached the crest.',
          'The ATV leveled out.',
          'Suddenly, the entire Mojave Desert opened before us.',
          'Miles of rolling dunes, rugged mountains, and winding trails stretched in every direction.',
          'We parked along the ridgeline and simply stood there for a few minutes, taking it all in.',
          'A few moments later, Alex made the same climb we had just conquered.',
          'What had felt monumental to us looked completely effortless for him.',
          'After one last look across the desert, we climbed back onto our ATVs.',
          'Five minutes later, we rolled back into the parking lot where the adventure had begun.',
        ],
      },
      {
        heading: 'The Ride Home',
        paragraphs: [
          'We returned our helmets, goggles, and gloves, climbed back into the shuttle, and started the drive toward Las Vegas.',
          'Only then did we fully appreciate what we had just done.',
          'Every one of us was covered in a fine layer of Mojave dust.',
          'It clung to our clothes, our shoes, our backpacks, and somehow found its way into every crease imaginable. There was no hiding where we had spent the afternoon.',
          'As the adrenaline wore off, another realization began to set in.',
          'I was sore.',
          'Not just tired, but the kind of sore you feel after a really good workout. My shoulders, arms, and upper body had spent the afternoon wrestling a 600-pound ATV across sand, gravel, and rock. Driving one was far more physical than I ever would have guessed.',
          'As we followed the dirt road from the staging area toward the highway, Alex suddenly slowed the shuttle and pulled off to the side.',
          '"Come check this out."',
          'We climbed out and gathered around as he pointed to a large desert spider making its way across the ground.',
          'It was a simple moment, but one that reminded us we had spent the afternoon riding through a real desert ecosystem, not just an off-road course built for tourists.',
          'Marcus stared at it for a second before saying, "If I\'d known those things were out here roaming around, I\'m not sure I would have done it."',
          'The van erupted in laughter.',
          'It was the perfect ending to the day.',
          'Before long, we were back on the highway, watching the Mojave slowly disappear in the rearview mirror as the Las Vegas skyline came back into view.',
          'We returned to re:Invent a little dustier, a little sorer, and carrying a story we would still be laughing about years later.',
        ],
      },
      {
        heading: 'Looking Back',
        paragraphs: [
          'At the time, this was simply an afternoon away from AWS re:Invent.',
          'Looking back, it became much more than that.',
          'It established a tradition that continues to shape Reckon today.',
          'Every major conference and every corporate retreat now includes time to step away from the agenda and share an experience together. Sometimes that is hiking through Zion National Park. Sometimes it is driving Jeeps through the Valley of Fire, snowmobiling across an Icelandic glacier, sailing in the Caribbean, or exploring a city we had never seen before.',
          'The adventure itself changes.',
          'The purpose never does.',
          'Shared adventures create shared stories.',
          'Shared stories build stronger teams.',
          'The Mojave Desert was where we first discovered that.',
          'Looking back, this was not just Reckon\'s first ATV ride.',
          'It was the first chapter of The Reckon Way.',
        ],
      },
    ],
    images: [
      {
        src: vegas2022Atv5314Image,
        alt: 'A Reckon crew member standing beside the ATVs during the 2022 Mojave Desert tour',
        position: 'center center',
      },
      {
        src: vegas2022Atv5315Image,
        alt: 'A Reckon crew member seated on an ATV in the Mojave Desert',
        position: 'center center',
      },
      {
        src: vegas2022Atv5318Image,
        alt: 'A Reckon crew member taking a break beside the ATVs in the Mojave Desert',
        position: 'center center',
      },
    ],
    websiteUrl: 'https://bestatvtourslv.com/',
  },
  'UTV Tour in the Mojave Desert': {
    description: 'Our second Mojave off-road adventure put six Reckon drivers behind the wheels of Can-Am Maverick X3 UTVs for more than thirty miles of desert trails.',
    whyWeWent: 'The ATV ride in 2022 had already become a highlight and the beginning of a Reckon tradition, so we returned in 2023 with more of the team ready to experience the desert together.',
    whatHappened: 'Las Vegas Elite Offroad led us on an extended two-hour drive through washes, rock gardens, dunes, and blind ridgelines, with everyone chasing 50 mph whenever the trail opened up.',
    whatWeRemember: 'The speed and climbs became one long rush, but Al calling his son afterward to say they had to return together captured why these shared adventures matter.',
    storySections: [
      {
        heading: 'A Tradition Worth Repeating',
        paragraphs: [
          'When we returned to AWS re:Invent in 2023, there wasn\'t much debate about what we\'d do outside the conference.',
          'The year before, four of us escaped the Las Vegas Strip for an ATV adventure through the Mojave Desert. What started as a way to get away from the conference had become one of the highlights of the trip and the beginning of a new Reckon tradition.',
          'This year, we weren\'t discovering it for the first time.',
          'We were bringing more of the team along.',
          'Joining us were Al and Matt, both experiencing one of our conference adventures for the first time. Along with Kevin, Marcus, Brandon A., and Brandon L., the six of us were ready to trade conference rooms for desert trails.',
          'Our adventure was scheduled for Thursday afternoon with pickup at the MGM Grand.',
          'Everyone met a little early, and before boarding the shuttle we wandered across Las Vegas Boulevard into New York-New York looking for breakfast. After walking around for a few minutes, we settled on a small crêpe shop. It wasn\'t a destination anyone would normally seek out, but those simple moments often become part of the story. Sitting around breakfast before an adventure has become just as much a tradition as the adventure itself.',
          'Outside the hotel, our shuttle from Las Vegas Elite Offroad was waiting.',
          'About fifteen minutes later we arrived at what looked like an ordinary industrial park near Nellis Air Force Base and the Las Vegas Motor Speedway. If you drove by, you\'d never guess this was where the afternoon\'s adventure would begin.',
          'Inside, we checked in, signed our waivers, and listened as our guides, Brandon and Dylan, walked everyone through the safety briefing. Helmets, goggles, and headsocks were handed out before we climbed back onto the shuttle for one final ride to the trailhead.',
          'When we stepped off the bus, a lineup of Can-Am Maverick X3 UTVs was waiting for us.',
          'Everything about them looked more serious than the ATVs we\'d driven the year before.',
          'Wide stance.',
          'Long-travel suspension.',
          'Aggressive tires.',
          'Full roll cages.',
          'These weren\'t machines built to casually explore the desert.',
          'They were built to attack it.',
          'Our convoy consisted of eight UTVs.',
          'Brandon led the group.',
          'Dylan brought up the rear.',
          'Between them were six Reckon drivers, each with a Maverick X3 of their own.',
          'Unlike many off-road tours where passengers ride together, everyone was behind the wheel.',
          'This time we\'d booked the extended tour.',
          'Nearly two hours of driving.',
          'About an hour into the Mojave.',
          'A short break.',
          'Then nearly another hour back along an entirely different route.',
          'More than thirty miles of desert lay ahead.',
        ],
      },
      {
        heading: 'Chasing Fifty',
        paragraphs: [
          'The biggest difference became obvious almost immediately.',
          'Speed.',
          'The ATVs had been fun.',
          'These were something else.',
          'As soon as Brandon pulled onto the trail, the pace picked up.',
          'The Maverick X3s seemed perfectly at home in the Mojave. They floated across terrain that would have rattled us around the year before. The suspension absorbed washboards, rocks, and ruts while the turbocharged engines always seemed to have more to give.',
          'The terrain constantly changed.',
          'Dry washes.',
          'Rock gardens.',
          'Open desert.',
          'Steep dunes.',
          'Every few minutes the landscape became something completely different.',
          'Whenever the trail opened up, everyone had the same goal.',
          'Hit fifty.',
          'We all found ourselves sneaking glances at the speedometer, hoping to see it climb past 50 mph before the next turn, wash, or hill demanded our attention.',
          'Later that evening, one of the first conversations we had wasn\'t about the conference.',
          'It was comparing top speeds.',
          'Not because it mattered.',
          'Because every one of us had been trying to do the exact same thing all afternoon.',
        ],
      },
      {
        heading: 'Trust the Crest',
        paragraphs: [
          'More than anything else, I remember the climbs.',
          'Not one particular hill.',
          'All of them.',
          'The desert rolled endlessly through dunes and ridgelines, and every blind crest brought the same feeling.',
          'The UTV ahead of you would begin climbing.',
          'Then disappear.',
          'For a few seconds, all you could see through the windshield was blue sky.',
          'You couldn\'t see the trail.',
          'You couldn\'t see Brandon.',
          'You couldn\'t see the driver in front of you.',
          'All you could do was trust that the guide had picked the right line, trust that the person ahead of you hadn\'t stopped, and keep your foot on the throttle.',
          'The Maverick would claw its way up the hill.',
          'The engine would pull.',
          'Then suddenly you\'d crest the ridge and the desert would open up in front of you once again.',
          'There was Brandon.',
          'Already leading us toward the next challenge.',
          'That feeling repeated itself over and over again throughout the afternoon.',
          'Every climb carried the same split second of uncertainty followed by the rush of seeing the trail stretch back out before you.',
        ],
      },
      {
        heading: 'A Moment of Quiet',
        paragraphs: [
          'About an hour into the ride, we stopped for a break.',
          'The engines fell silent.',
          'Helmets came off.',
          'Goggles were pushed onto foreheads.',
          'Cold drinks never tasted so good.',
          'Standing in the middle of the Mojave, the silence was almost surprising.',
          'Then it was broken by the unmistakable sound of military jets overhead.',
          'If memory serves, they were a pair of F-22 Raptors from nearby Nellis Air Force Base, roaring across the desert before disappearing into the distance.',
          'It was one of those moments you couldn\'t have planned.',
          'A few minutes later, the helmets went back on, the engines came to life, and we headed out again.',
          'The return trip followed an entirely different set of trails, making the second hour feel like a brand-new adventure instead of simply retracing our steps.',
        ],
      },
      {
        heading: 'One Long Rush',
        paragraphs: [
          'Looking back, this adventure feels different than our first trip into the Mojave.',
          'The ATV ride is easy to replay in my mind.',
          'I remember specific trails.',
          'Specific conversations.',
          'Specific moments.',
          'This one is different.',
          'The memories blur together.',
          'Not because it wasn\'t memorable.',
          'Because it was so immersive.',
          'What I remember is the feeling.',
          'The speed.',
          'The dust.',
          'The vibration.',
          'The constant focus of reading the terrain while trying to keep pace with Brandon and Dylan.',
          'Two hours.',
          'More than thirty miles.',
          'A blur of washes, rocks, dunes, and open desert.',
          'It wasn\'t one unforgettable moment.',
          'It was two hours of them.',
        ],
      },
      {
        heading: 'The Ride Home',
        paragraphs: [
          'On the shuttle back to the MGM Grand, everyone compared speeds, laughed about the climbs, and replayed their favorite sections of the trail.',
          'One moment has stayed with me more than any other.',
          'Al pulled out his phone and called his son.',
          '"You\'ll never believe what I just did."',
          'A few moments later he smiled and said,',
          '"We have to come back and do this together."',
          'That conversation captured exactly why we started adding adventures to our conference trips.',
          'The UTVs were incredible.',
          'The desert was unforgettable.',
          'But hearing someone immediately think about sharing the experience with his family reminded me what these trips are really about.',
          'Great experiences don\'t end when you take off your helmet.',
          'They become stories.',
          'Stories become traditions.',
          'Traditions become culture.',
          'Looking back, our second trip into the Mojave wasn\'t simply a faster version of the first.',
          'It was the moment we realized this wasn\'t becoming a Reckon tradition.',
          'It already was.',
        ],
      },
    ],
    images: [
      {
        src: utvCrewGroupImage,
        alt: 'The Reckon crew gathered around blue UTVs in the Mojave Desert',
        position: 'center center',
      },
      {
        src: utvDriverImage,
        alt: 'A helmeted Reckon crew member seated behind the wheel of a blue UTV',
        position: 'center center',
      },
      {
        src: utvTrailBreakImage,
        alt: 'The Reckon crew taking a break together on the Mojave Desert trail',
        position: 'center center',
      },
      {
        src: utvGearUpImage,
        alt: 'The Reckon crew gearing up with helmets before the UTV tour',
        position: 'center center',
      },
      {
        src: utvTrailBriefingImage,
        alt: 'The Reckon crew listening to the UTV trail briefing in the Mojave Desert',
        position: 'center center',
      },
      {
        src: utvDriverViewImage,
        alt: 'A driver\'s view from inside a UTV looking across the Mojave Desert',
        position: 'center center',
      },
      {
        src: utvBehindTheWheelImage,
        alt: 'A helmeted Reckon crew member behind the wheel of a UTV',
        position: 'center center',
      },
    ],
    websiteUrl: 'https://www.lasvegaseliteoffroad.com/',
  },
  'U2 at Sphere': {
    description: 'U2 brought Achtung Baby to life inside Sphere during the Reckon crew\'s first visit to the venue in 2023.',
    dayLabel: '12/1/2023',
    whatWeRemember: 'A full Achtung Baby performance, an acoustic mid-show break, and an encore that closed with some of U2\'s biggest songs—all surrounded by Sphere\'s immersive visuals.',
    storySections: [
      {
        heading: 'Achtung Baby (First Half)',
        items: [
          'Zoo Station',
          'The Fly',
          'Even Better Than the Real Thing',
          'Mysterious Ways',
          'One',
          'Until the End of the World',
          'Who\'s Gonna Ride Your Wild Horses',
          'Tryin\' to Throw Your Arms Around the World',
        ],
      },
      {
        heading: 'Acoustic / Mid-Show Break',
        items: [
          'All I Want Is You',
          'A Rainy Night in Soho (The Pogues cover, dedicated to Shane MacGowan)',
          'Desire',
          'Two Hearts Beat as One',
          'Sunday Bloody Sunday',
        ],
      },
      {
        heading: 'Achtung Baby (Second Half)',
        items: [
          'Acrobat',
          'So Cruel',
          'Ultraviolet (Light My Way)',
          'Love Is Blindness',
        ],
      },
      {
        heading: 'Encore',
        items: [
          'Elevation',
          'Atomic City',
          'Vertigo',
          'Where the Streets Have No Name',
          'With or Without You',
          'Beautiful Day',
        ],
      },
    ],
    relatedMediaIds: ['u2-sphere-crew-2023', 'u2-sphere-concert-2023'],
    images: [
      {
        src: u2SphereCrewImage,
        alt: 'The Reckon crew beneath the U2 UV Achtung Baby Live at Sphere display',
        position: 'center center',
      },
      {
        src: u2SphereConcertImage,
        alt: 'U2 performing beneath an immersive display of birds and butterflies inside Sphere',
        position: 'center center',
      },
    ],
  },
  'Jeep Off-Road Tour in Valley of Fire': {
    images: valleyOfFireGallery,
    websiteUrl: 'https://lasvegasrockcrawlers.com/',
  },
  'Hiking Zion National Park': {
    description: 'Three hikes, one unforgettable day in Zion National Park—with the road to Springdale, a star-filled first night, and a guided journey through the canyon all part of the story.',
    dayLabel: 'November 30–December 1, 2024',
    location: 'Zion National Park and Springdale, Utah',
    whyWeWent: 'Zion had been on our bucket list for years, and our annual AWS re:Invent trip finally put the Reckon crew within a few hours of the canyon.',
    whatHappened: 'With our guide Samantha, we climbed to Scout Lookout, followed the Virgin River on Riverside Walk, and raced the sunset up Watchman Trail.',
    whatWeRemember: 'The views were unforgettable, but so were the quieter moments: dinner in Springdale, stars over the hotel, a homemade trail lunch, and reaching every overlook together.',
    storyPages: zionStoryPages,
    storyLinks: [
      { label: 'Read Overview', storyPageSlug: 'overview' },
      { label: 'Read Day 1', storyPageSlug: 'day-1' },
      { label: 'Read Day 2', storyPageSlug: 'day-2' },
    ],
    images: zionHikeGallery,
    videos: [
      {
        id: 'zion-hike-video-100612',
        firebaseAssetId: 'zion-hike-video-100612',
        type: 'video',
        mimeType: 'video/mp4',
        captionsSrc: '/captions/aws-reinvent-2024-zion-hike-100612.vtt',
        alt: 'Hiking the Zion National Park trail during AWS re:Invent 2024',
        title: 'On the Trail in Zion',
      },
      {
        id: 'zion-hike-video-130042',
        firebaseAssetId: 'zion-hike-video-130042',
        type: 'video',
        mimeType: 'video/mp4',
        captionsSrc: '/captions/aws-reinvent-2024-zion-hike-130042.vtt',
        alt: 'Zion National Park trail views during AWS re:Invent 2024',
        title: 'Zion Trail Views',
      },
    ],
    websiteUrl: 'https://wildlandtrekking.com/',
  },
  'Eagles at Sphere': {
    description: 'The Eagles brought a career-spanning residency performance to Sphere during the Reckon crew\'s 2024 Las Vegas trip.',
    dayLabel: '12/6/2024',
    whatWeRemember: 'A 20-song journey through the Eagles catalog, opening with Hotel California and closing with an encore of Take It Easy, Rocky Mountain Way, Desperado, and Heartache Tonight.',
    storySections: [
      {
        heading: 'Main Set',
        items: [
          'Hotel California',
          'One of These Nights',
          'Lyin\' Eyes',
          'Take It to the Limit',
          'Witchy Woman',
          'Peaceful Easy Feeling',
          'Tequila Sunrise',
          'In the City',
          'I Can\'t Tell You Why',
          'New Kid in Town',
          'Seven Bridges Road',
          'Those Shoes',
          'Life\'s Been Good',
          'Already Gone',
          'The Boys of Summer',
          'Life in the Fast Lane',
        ],
      },
      {
        heading: 'Encore',
        items: [
          'Take It Easy',
          'Rocky Mountain Way',
          'Desperado',
          'Heartache Tonight',
        ],
      },
    ],
    relatedMediaIds: ['eagles-at-sphere-group', 'eagles-at-sphere-concert'],
    images: [
      {
        src: eaglesAtSphereGroupImage,
        alt: 'The Reckon crew beneath the Eagles Live at Sphere display in Las Vegas',
        position: 'center center',
      },
      {
        src: eaglesAtSphereConcertImage,
        alt: 'The Eagles performing beneath an immersive wall of imagery inside Sphere',
        position: 'center center',
      },
    ],
  },
  'Whiskeys of the World Tasting': {
    images: [
      {
        src: whiskeyTastingCrewImage,
        alt: 'The Reckon crew raising glasses together during the whiskey tasting',
        position: 'center center',
      },
      {
        src: whiskeysOfTheWorldImage,
        alt: 'A black-and-white lineup of whiskey bottles selected for the tasting',
        position: 'center bottom',
      },
    ],
    websiteUrl: 'https://www.whiskyattic.com/',
  },
};
const activities = getRecurringActivities(years, activityMetadata).map((activity) => ({
  ...activity,
  ...(activityDisplayMetadata[activity.title] || {}),
}));
const restaurantMetadata = {
  STK: {
    description: 'STK has been our traditional re:Invent kickoff since it is right inside The Cosmopolitan, where we stay. We keep coming back for the energy, the atmosphere, and, of course, the food.',
    image: stkDinner2024Image,
    imageAlt: 'The Reckon crew gathered around the dinner table at STK Las Vegas in 2024',
    imagePosition: 'center center',
    websiteUrl: 'https://stksteakhouse.com/en-us/location/las-vegas/',
  },
  'Amalfi by Bobby Flay': {
    description: 'Bobby Flay brings the flavors of the Amalfi Coast to Caesars Palace through fresh seafood, handmade pasta, bright Mediterranean ingredients, and an easy coastal atmosphere.',
    image: vegas2022Amalfi5383Image,
    imageAlt: 'The Reckon crew visiting the seafood market at Amalfi by Bobby Flay in 2022',
    imagePosition: 'center center',
    websiteUrl: 'https://www.caesars.com/caesars-palace/restaurants/amalfi',
  },
  'Bazaar Meat': {
    description: 'We love the food and the atmosphere. Who does not love having the grills and kitchen integrated into the dining room? The presentation is always creative, and its location near the fairgrounds makes it our ideal Thursday dinner before AWS re:Play.',
    image: bazaarSteakImage,
    imageAlt: 'A sliced steak presented on a stone platter at Bazaar Meat in Las Vegas',
    imagePosition: 'center center',
    websiteUrl: 'https://www.thebazaar.com/location/las-vegas/',
  },
  Catch: {
    description: 'We found this Las Vegas hot spot in 2023 and loved it so much that we came back in 2024. Brandon A. even claims he "ran" into Mike Tyson there.',
    image: catchTeamDinnerImage,
    imageAlt: 'The Reckon crew gathered around the table for dinner at Catch in Las Vegas',
    imagePosition: 'center center',
    websiteUrl: 'https://www.catchrestaurants.com/location/catch-las-vegas/',
  },
  Brezza: {
    description: 'Brezza brings modern coastal Italian cooking to Resorts World, with housemade pasta, premier seafood, seasonal ingredients, and signature Negronis.',
    image: brezzaDinnerImage,
    imageAlt: 'The Reckon crew sharing a coastal Italian spread at Brezza in Las Vegas',
    imagePosition: 'center center',
    websiteUrl: 'https://www.rwlasvegas.com/dining/brezza/',
  },
  'Golden Steer': {
    description: 'Golden Steer is a step into classic Las Vegas, with old-school booths, tableside presentations, prime steaks, and decades of Rat Pack history dating back to 1958.',
    image: goldenSteerGroupImage,
    imageAlt: 'The Reckon crew gathered beneath the Golden Steer marquee in Las Vegas',
    imagePosition: 'center center',
    websiteUrl: 'https://goldensteer.com/pages/las-vegas',
  },
  'Don’s Prime': {
    description: 'Don’s Prime pairs a glamorous Fontainebleau dining room with prime steaks, Japanese wagyu, seafood, and the polished service of a modern Las Vegas steakhouse.',
    image: donsPrimeDinnerImage,
    imageAlt: 'The Reckon crew sharing an elaborate seafood spread at Don’s Prime in Las Vegas',
    imagePosition: 'center center',
    websiteUrl: 'https://www.fontainebleaulasvegas.com/dining/restaurants/dons-prime/',
  },
  'Estiatorio Milos': {
    description: 'Milos centers the experience on pristine Mediterranean seafood, inviting guests to choose whole fish from the market alongside vibrant Greek produce and a celebrated raw bar.',
    image: estiatorioMilosSeafoodImage,
    imageAlt: 'A market display of whole Mediterranean fish on ice at Estiatorio Milos in Las Vegas',
    imagePosition: 'center center',
    websiteUrl: 'https://www.estiatoriomilos.com/location/lasvegas/',
  },
  'Jean-Georges Steakhouse': {
    description: 'Jean-Georges gives the classic steakhouse a modern, globally inspired edge through exceptional meats, seafood, inventive sauces, and a bold dining room at ARIA.',
    websiteUrl: 'https://www.jean-georges.com/restaurants/united-states/las-vegas/jean-georges-steakhouse/',
  },
};
const restaurantVisits = getRecurringRestaurantVisits(years).map((restaurant) => ({
  ...restaurant,
  ...(restaurantMetadata[restaurant.name] || {}),
}));
const badgesByYear = {
  'atvs-mojave-desert': 2022,
  'utvs-mojave-desert': 2023,
  'u2-at-sphere': 2023,
  'valley-of-fire-off-roading': 2024,
  'hiking-zion-national-park': 2024,
  'eagles-at-sphere': 2024,
};

const awsReinvent = defineReckonEvent({
  id: 'aws-reinvent-las-vegas',
  slug: 'aws-reinvent-las-vegas',
  title: 'AWS re:Invent',
  subtitle: 'An annual Reckon tradition built around cloud technology, unforgettable dinners, and adventures beyond the conference floor.',
  eventType: 'conference',
  category: 'Recurring Conference',
  classification: 'Recurring Conference',
  recurring: true,
  status: 'published',
  destination: 'Las Vegas, Nevada',
  city: 'Las Vegas',
  region: 'Nevada',
  country: 'United States',
  yearsLabel: '2022 to 2025',
  heroImage: {
    id: 'aws-reinvent-hero',
    src: awsReinventHero,
    alt: 'AWS re:Invent in Las Vegas, part of The Reckon Way',
    position: 'center center',
  },
  summary: 'AWS re:Invent has become one of Reckon’s recurring conference traditions. Each trip combines the scale and energy of the conference with team dinners, outdoor adventures, live entertainment, and time together away from the office. Rather than treating each year as a separate trip, this page follows the experience as it evolves. New team members join, favorite restaurants return, and every year adds another chapter to the story.',
  summaryParagraphs: [
    'AWS re:Invent has become one of Reckon’s recurring conference traditions. Each trip combines the scale and energy of the conference with team dinners, outdoor adventures, live entertainment, and time together away from the office.',
    'Rather than treating each year as a separate trip, this page follows the experience as it evolves. New team members join, favorite restaurants return, and every year adds another chapter to the story.',
  ],
  years,
  activities,
  restaurantVisits,
  recurringCrew: getRecurringAttendees(years, crewDirectory),
  atAGlance: getRecurringEventStats(years, activities),
  typicalHotel: {
    name: 'The Cosmopolitan of Las Vegas',
    label: 'Typical re:Invent stay',
    description: "The Cosmopolitan is the Reckon Crew's usual home base during AWS re:Invent, putting us in the middle of the Las Vegas Strip for conference days, team dinners, and everything that happens after the sessions end.",
    highlights: [
      'Our usual re:Invent hotel',
      'Central Las Vegas Strip location',
      'A familiar base for the week',
    ],
  },
  traditions: [
    { title: 'STK', description: 'A recurring stop during Reckon’s first three years at re:Invent.', years: [2022, 2023, 2024] },
    { title: 'Bazaar Meat', description: 'A return favorite during the 2022 and 2023 trips.', years: [2022, 2023] },
    { title: 'Catch', description: 'Part of the re:Invent dining lineup in 2023 and 2024.', years: [2023, 2024] },
    { title: 'Sphere', description: 'The crew experienced two major concerts at Sphere across consecutive years.', years: [2023, 2024] },
    { title: 'Mojave Off-Roading', description: 'ATVs in 2022 were followed by UTVs in 2023.', years: [2022, 2023] },
  ],
  meritBadges: achievementRefs(
    'atvs-mojave-desert',
    'utvs-mojave-desert',
    'u2-at-sphere',
    'valley-of-fire-off-roading',
    'hiking-zion-national-park',
    'eagles-at-sphere',
  ).map((badge) => ({ ...badge, year: badgesByYear[badge.slug] })),
  gallery: [
    {
      id: 'stk-crew-table-2022-5298',
      src: vegas2022Stk5298Image,
      alt: 'The 2022 Reckon crew gathered around the table at STK Las Vegas',
      caption: 'The first re:Invent crew kicking off the 2022 trip at STK.',
    },
    {
      id: 'stk-steaks-2022-5302',
      src: vegas2022Stk5302Image,
      alt: 'Steaks, sides, and wine spread across the table at STK Las Vegas',
      caption: 'The food that helped make STK our traditional re:Invent kickoff.',
    },
    {
      id: 'atv-break-2022-5314',
      src: vegas2022Atv5314Image,
      alt: 'A Reckon crew member standing beside the ATVs during the 2022 Mojave Desert tour',
      caption: 'A quick break beside the ATVs in the Mojave Desert.',
    },
    {
      id: 'atv-rider-2022-5315',
      src: vegas2022Atv5315Image,
      alt: 'A Reckon crew member seated on an ATV in the Mojave Desert',
      caption: 'Ready to get back onto the Mojave Desert trail.',
    },
    {
      id: 'atv-trail-break-2022-5318',
      src: vegas2022Atv5318Image,
      alt: 'A Reckon crew member taking a break beside the ATVs in the Mojave Desert',
      caption: 'Blue skies and open desert during the 2022 ATV tour.',
    },
    {
      id: 'amalfi-seafood-market-2022-5383',
      src: vegas2022Amalfi5383Image,
      alt: 'The Reckon crew visiting the seafood market at Amalfi by Bobby Flay in 2022',
      caption: 'Choosing from the seafood market at Amalfi by Bobby Flay.',
    },
    {
      id: 'bazaar-meat-locker-2022-5396',
      src: vegas2022Bazaar5396Image,
      alt: 'The dry-aged beef display at Bazaar Meat in Las Vegas',
      caption: 'The dry-aged beef display set the stage at Bazaar Meat in 2022.',
    },
    {
      id: 'bazaar-meat-crew-2022-5398',
      src: vegas2022Bazaar5398Image,
      alt: 'Members of the Reckon crew at dinner inside Bazaar Meat in 2022',
      caption: 'The first Reckon visit to what became a return favorite.',
    },
    {
      id: 'bazaar-meat-steak-2023',
      src: bazaarSteakImage,
      alt: 'A sliced steak presented on a stone platter at Bazaar Meat in Las Vegas',
      caption: 'The centerpiece at Bazaar Meat during re:Invent 2023.',
    },
    {
      id: 'bazaar-meat-opening-bites-2023',
      src: bazaarBitesImage,
      alt: 'Creative opening bites presented on a black stone platter at Bazaar Meat',
      caption: 'Creative presentation from the opening bites at Bazaar Meat.',
    },
    {
      id: 'bazaar-meat-cotton-candy-2023',
      src: bazaarCottonCandyImage,
      alt: 'Cotton candy served on individual sticks at Bazaar Meat',
      caption: 'Cotton candy foie gras made for a theatrical start to dinner at Bazaar Meat.',
    },
    {
      id: 'brezza-team-dinner-2024',
      src: brezzaDinnerImage,
      alt: 'The Reckon crew sharing a coastal Italian spread at Brezza in Las Vegas',
      caption: 'A table full of coastal Italian dishes at Brezza during re:Invent 2024.',
    },
    {
      id: 'dons-prime-seafood-spread-2024',
      src: donsPrimeDinnerImage,
      alt: 'The Reckon crew sharing an elaborate seafood spread at Don’s Prime in Las Vegas',
      caption: 'The seafood spread took over the table at Don’s Prime during re:Invent 2024.',
    },
    {
      id: 'stk-team-dinner-2023',
      src: stkDinnerImage,
      alt: 'The Reckon crew gathered around the dinner table at STK Las Vegas',
      caption: 'The 2023 re:Invent kickoff dinner at STK.',
    },
    {
      id: 'stk-crew-2023',
      src: stkCrewImage,
      alt: 'The Reckon crew together inside STK Las Vegas',
      caption: 'The crew soaking up the energy at STK.',
    },
    {
      id: 'stk-toast-2023',
      src: stkToastImage,
      alt: 'The Reckon crew raising a toast inside STK Las Vegas',
      caption: 'A toast to the start of another re:Invent week.',
    },
    {
      id: 'stk-team-dinner-2024',
      src: stkDinner2024Image,
      alt: 'The Reckon crew gathered around the dinner table at STK Las Vegas in 2024',
      caption: 'Another re:Invent week kicked off together at STK in 2024.',
    },
    {
      id: 'utv-crew-group',
      src: utvCrewGroupImage,
      alt: 'The Reckon crew gathered around blue UTVs in the Mojave Desert',
      caption: 'The 2023 Reckon crew and their UTVs in the Mojave Desert.',
    },
    {
      id: 'utv-driver',
      src: utvDriverImage,
      alt: 'A helmeted Reckon crew member seated behind the wheel of a blue UTV',
      caption: 'Ready to head out onto the desert trail.',
    },
    {
      id: 'utv-trail-break',
      src: utvTrailBreakImage,
      alt: 'The Reckon crew taking a break together on the Mojave Desert trail',
      caption: 'A trail break during the 2023 UTV adventure.',
    },
    {
      id: 'utv-gearing-up',
      src: utvGearUpImage,
      alt: 'The Reckon crew gearing up with helmets before the UTV tour',
      caption: 'Gearing up before heading into the Mojave Desert.',
    },
    {
      id: 'utv-trail-briefing',
      src: utvTrailBriefingImage,
      alt: 'The Reckon crew listening to the UTV trail briefing in the Mojave Desert',
      caption: 'One last trail briefing before the engines started.',
    },
    {
      id: 'utv-driver-view',
      src: utvDriverViewImage,
      alt: 'A driver\'s view from inside a UTV looking across the Mojave Desert',
      caption: 'The Mojave Desert from behind the wheel.',
    },
    {
      id: 'utv-behind-the-wheel',
      src: utvBehindTheWheelImage,
      alt: 'A helmeted Reckon crew member behind the wheel of a UTV',
      caption: 'Helmet on and ready to ride.',
    },
    {
      id: 'u2-sphere-crew-2023',
      src: u2SphereCrewImage,
      alt: 'The Reckon crew beneath the U2 UV Achtung Baby Live at Sphere display',
      caption: 'The crew at Sphere for U2 in 2023.',
    },
    {
      id: 'u2-sphere-concert-2023',
      src: u2SphereConcertImage,
      alt: 'U2 performing beneath an immersive display of birds and butterflies inside Sphere',
      caption: 'U2 surrounded by the immersive scale of Sphere.',
    },
    {
      id: 'catch-team-dinner',
      src: catchTeamDinnerImage,
      alt: 'The Reckon crew gathered around the table for dinner at Catch in Las Vegas',
      caption: 'Dinner together at Catch during re:Invent 2024.',
    },
    {
      id: 'catch-flower-wall-2023',
      src: catchFlowerWallImage,
      alt: 'The Reckon crew seated beneath the flower-covered Catch sign in Las Vegas',
      caption: 'The crew at Catch during the first visit in 2023.',
    },
    {
      id: 'catch-team-dinner-2023',
      src: catchDinner2023Image,
      alt: 'The Reckon crew gathered around the table at Catch in Las Vegas',
      caption: 'Around the table at Catch during re:Invent 2023.',
    },
    {
      id: 'golden-steer-group',
      src: goldenSteerGroupImage,
      alt: 'The Reckon crew gathered beneath the Golden Steer marquee in Las Vegas',
      caption: 'The crew outside Golden Steer after a classic Las Vegas dinner.',
    },
    {
      id: 'estiatorio-milos-seafood-market-2024',
      src: estiatorioMilosSeafoodImage,
      alt: 'A market display of whole Mediterranean fish on ice at Estiatorio Milos in Las Vegas',
      caption: 'Choosing from the seafood market at Estiatorio Milos during re:Invent 2024.',
    },
    {
      id: 'eagles-at-sphere-group',
      src: eaglesAtSphereGroupImage,
      alt: 'The Reckon crew beneath the Eagles Live at Sphere display in Las Vegas',
      caption: 'The Reckon crew at Sphere for the Eagles in 2024.',
    },
    {
      id: 'eagles-at-sphere-concert',
      src: eaglesAtSphereConcertImage,
      alt: 'The Eagles performing beneath an immersive wall of imagery inside Sphere',
      caption: 'The Eagles surrounded by the full visual scale of Sphere.',
    },
    {
      id: 'whiskeys-of-the-world-bottles',
      src: whiskeysOfTheWorldImage,
      alt: 'A black-and-white lineup of whiskey bottles selected for the tasting',
      caption: 'A tour through whiskeys from around the world during re:Invent 2024.',
    },
    {
      id: 'whiskeys-of-the-world-crew',
      src: whiskeyTastingCrewImage,
      alt: 'The Reckon crew raising glasses together during the whiskey tasting',
      caption: 'Raising a glass together during the Whisky Attic tasting.',
    },
    ...valleyOfFireGallery,
    ...zionHikeGallery,
    {
      id: 'zion-hike-video-100612',
      firebaseAssetId: 'zion-hike-video-100612',
      type: 'video',
      mimeType: 'video/mp4',
      captionsSrc: '/captions/aws-reinvent-2024-zion-hike-100612.vtt',
      alt: 'Hiking the Zion National Park trail during AWS re:Invent 2024',
      caption: 'On the trail during the 2024 Zion National Park hike.',
    },
    {
      id: 'zion-hike-video-130042',
      firebaseAssetId: 'zion-hike-video-130042',
      type: 'video',
      mimeType: 'video/mp4',
      captionsSrc: '/captions/aws-reinvent-2024-zion-hike-130042.vtt',
      alt: 'Zion National Park trail views during AWS re:Invent 2024',
      caption: 'Taking in the Zion trail views with the Reckon crew.',
    },
  ],
  tags: tagRefs(
    'conference',
    'annual-event',
    'adventure',
    'team-building',
    'united-states',
    'city-exploration',
    'fine-dining',
    'steakhouse',
    'live-music',
    'hiking',
    'conference-series',
    'best-restaurants',
  ),
  heroTags: ['Conference', 'AWS', 'Las Vegas', 'Technology', 'Team Travel', 'Dining', 'Adventure', 'Recurring Event'],
});

export default awsReinvent;
